import {
  ArticleApiResponse,
  CollectionApiResponse,
  KLArticleApiResponse,
  StoryApiResponse,
} from 'api/api.types';
import {
  Box,
  CaretRightSvg,
  color,
  Flex,
  Link,
  PrimaryHeading,
  PrimaryText,
  spacing,
  Text,
  useColorMode,
  useIsMobileView,
} from 'deepstash-ui';
import useRouter from 'hooks/useRouter';
import React, { useCallback, useEffect } from 'react';
import LayoutAdapter from 'src/components/layout/LayoutAdapter';
import NextLink from 'src/components/navigation/NextLink';
import { Analytics, Events } from 'src/services/analytics';
import { KlList } from 'types/enums';
import { PageMetadata } from 'types/metadataTypes';
import { groupCollectionCollapsedIntoRows } from 'utils/collection.utils';
import {
  GAP_SIZE_REM,
  getDomainName,
  transformArrayToMatrix,
} from 'utils/global';
import { normalizeCollections } from 'utils/normalizers/collection';
import { normalizeKLSource } from 'utils/normalizers/knowledgeLibrary';
import CollectionCard from '../collection/CollectionCard';
import SourceCollapsed from '../source/source-collapsed/SourceCollapsed';
import StoryCard from '../story/StoryCard';
import ScrollWrapperOriginal from './components/ScrollWrapperOriginal';

interface LatestList<T> {
  list: T[];
  numberOfPages: number;
}

interface OwnProps {
  data: LatestList<
    KLArticleApiResponse | CollectionApiResponse | StoryApiResponse
  >;
  currentSectionState: KlList;
}

const KnowledgeLibrary: React.FC<OwnProps> = ({
  data,
  currentSectionState,
}) => {
  const router = useRouter();
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();

  const getArticlesDescriptionItems = useCallback(() => {
    if (!data) return { article1: '', domain1: '', domain2: '' };
    const sortedArticles = [
      ...(data as LatestList<ArticleApiResponse>).list,
    ].sort((a, b) => (a.title?.length || 0) - (b.title?.length || 0));
    let i = 0;
    while (
      (sortedArticles[i]?.title?.length || 0) === 0 &&
      i < sortedArticles.length - 1
    )
      i++;

    const domainUrl1 = sortedArticles[i]
      ? getDomainName(sortedArticles[i]?.url ?? '')
      : '';
    const domainUrl2 = sortedArticles[i + 1].url
      ? getDomainName(sortedArticles[i + 1]?.url ?? '')
      : '';
    return {
      article1: sortedArticles[i]?.title,
      domain1: domainUrl1.charAt(0)?.toUpperCase() + domainUrl1.slice(1),
      domain2: domainUrl2.charAt(0)?.toUpperCase() + domainUrl2.slice(1),
    };
  }, [data]);

  const pageNumber = parseInt(
    typeof router.query.page === 'string' ? router.query.page : '1',
  );

  useEffect(() => {
    Analytics.logEvent({
      eventName: Events.consumption.viewKL,
      properties: {
        type: currentSectionState,
        page: pageNumber,
      },
      platforms: ['amplitude'],
    });
  }, []);

  if (!data) return null;

  const menuOptions = [
    {
      title: 'Collections',
      key: 'collections',
    },
    {
      title: 'Articles',
      key: 'articles',
    },
    {
      title: 'Stories',
      key: 'stories',
    },
  ];

  const renderHeader = () => {
    return (
      <Flex
        width="100%"
        align={isMobileView ? 'flex-start' : 'center'}
        justifyContent="space-between"
        flexDir={isMobileView ? 'column' : 'row'}
        mb={GAP_SIZE_REM}
        zIndex={2}
      >
        <PrimaryHeading
          size="h1"
          textSize="l"
          type="bold"
          fontSize="32px"
          zIndex={1}
          color={color[colorMode].text}
          lineHeight="40px"
        >
          {klPageMetadata.pageTitle}
        </PrimaryHeading>
        <Flex maxWidth="100%" flexWrap="wrap">
          {menuOptions.map(menuOption => (
            <NextLink
              key={menuOption.key}
              href={
                menuOption.key === 'stories'
                  ? '/stories'
                  : '/' + menuOption.key + '/1'
              }
              as={
                menuOption.key === 'stories'
                  ? '/stories'
                  : '/' + menuOption.key + '/1'
              }
            >
              <Box
                px={isMobileView ? spacing.XS.rem : spacing.M.rem}
                py={isMobileView ? spacing.XXS.rem : spacing.XS.rem}
                mr={isMobileView ? spacing.XXS.rem : spacing.XS.rem}
                borderRadius={spacing.L.rem}
                backgroundColor={
                  currentSectionState === menuOption.key
                    ? color[colorMode].top
                    : color.transparent
                }
                _hover={{
                  cursor: 'pointer',
                  backgroundColor: color[colorMode].top,
                }}
                _active={undefined}
                _focus={undefined}
              >
                <Link
                  title={menuOption.title}
                  _hover={undefined}
                  _focus={undefined}
                  href={
                    menuOption.key === 'stories'
                      ? '/stories'
                      : '/' + menuOption.key + '/1'
                  }
                >
                  <PrimaryText
                    size={isMobileView ? 's' : 'm'}
                    color={color[colorMode].textSecondary}
                  >
                    {menuOption.title}
                  </PrimaryText>
                </Link>
              </Box>
            </NextLink>
          ))}
        </Flex>
      </Flex>
    );
  };

  const articleItems = () => {
    return (data as LatestList<KLArticleApiResponse>).list.map(article => (
      <SourceCollapsed
        key={`latestArticle$${article.id}`}
        source={normalizeKLSource(article)}
        mb={spacing.M.rem}
        width="100%"
      />
    ));
  };

  const collectionItems = () => {
    const collectionRows = groupCollectionCollapsedIntoRows({
      collections: normalizeCollections(
        (data as LatestList<CollectionApiResponse>).list ?? [],
      ),
      rowSize: 3,
    });

    return collectionRows.map((collections, index) => (
      <Flex
        key={`row-collections-${index}`}
        // bgColor="RED"
        width={'100%'}
        justifyContent={'space-between'}
        mt={index > 0 ? 32 : 0}
      >
        {collections.map((collection, index) => {
          return (
            <CollectionCard
              collection={{
                id: collection.id,
                image: collection.imageUrl,
                image_blurhash: collection.imageBlurhash,
                name: collection.name,
                total_blocks: collection.ideaCount,
              }}
              key={`collection-${index}`}
            />
          );
        })}
      </Flex>
    ));
  };

  const storiesItems = () => {
    const storyRows = transformArrayToMatrix({
      items: (data as LatestList<StoryApiResponse>).list ?? [],
      rowSize: 3,
    });

    return storyRows.map((stories, index) => (
      <Flex
        key={`row-story-${index}`}
        width={'100%'}
        justifyContent={'space-between'}
        mt={index > 0 ? 48 : 0}
        gridColumnGap={16}
      >
        {stories.map((story, index) => {
          return (
            <StoryCard
              key={`story-${index}`}
              story={story}
              imageWidth={'100%'}
              imageHeight={160}
            />
          );
        })}
      </Flex>
    ));
  };

  const getItems = (currentSection: string) => {
    switch (currentSection) {
      case KlList.ARTICLES:
        return articleItems();
      case KlList.COLLECTIONS:
        return collectionItems();
      case KlList.STORIES:
        return storiesItems();
      default:
        return [];
    }
  };

  const renderSection = (currentSection: string) => {
    return (
      <Flex flexDirection="column" mb="60px">
        <ScrollWrapperOriginal
          items={getItems(currentSection)}
          displayGrid={false}
        />
      </Flex>
    );
  };

  const renderPages = () => {
    const arr: number[] = [];

    const PAGES_MAX_NAVIGATION = isMobileView ? 1 : 3;

    if (pageNumber > PAGES_MAX_NAVIGATION + 1) {
      arr.push(1);
    }
    if (pageNumber > PAGES_MAX_NAVIGATION + 2) {
      arr.push(-1);
    }
    for (
      let i = Math.max(1, pageNumber - PAGES_MAX_NAVIGATION);
      i <= Math.min(pageNumber + PAGES_MAX_NAVIGATION, data.numberOfPages);
      i++
    ) {
      arr.push(i);
    }
    if (pageNumber < data.numberOfPages - PAGES_MAX_NAVIGATION - 1) {
      arr.push(-1);
    }
    if (pageNumber < data.numberOfPages - PAGES_MAX_NAVIGATION) {
      arr.push(data.numberOfPages);
    }

    return (
      <Flex maxW="100%" alignSelf={'center'} mb={spacing.XXXL.rem}>
        <NextLink
          href={
            router.asPath === '/stories/2'
              ? '/stories'
              : '/[currentSectionState]/[page]'
          }
          visibility={pageNumber > 1 ? 'visible' : 'hidden'}
          as={
            router.asPath === '/stories/2'
              ? '/stories'
              : `/${currentSectionState}/${pageNumber - 1}`
          }
        >
          <Link
            _hover={undefined}
            _focus={undefined}
            href={
              router.asPath === '/stories/2'
                ? '/stories'
                : `/${currentSectionState}/${pageNumber - 1}`
            }
            color={color[colorMode].text}
            title={(pageNumber - 1).toString()}
          >
            <Flex
              transform={'rotate(180deg)'}
              _hover={{ cursor: 'pointer' }}
              px={spacing.XS.rem}
              py={spacing.toRem(6)}
              mr={spacing.XS.rem}
              borderRadius={spacing.L.rem}
              align={'center'}
              justifyContent={'center'}
              color={color[colorMode].textSecondary}
            >
              <CaretRightSvg />
            </Flex>
          </Link>
        </NextLink>

        {arr.map((element, index) =>
          element === -1 ? (
            <Box px={spacing.M.rem} py={spacing.XS.rem} mr={spacing.XS.rem}>
              <Text color={color[colorMode].text}>...</Text>
            </Box>
          ) : (
            <NextLink
              key={`pageIndex${index}`}
              href={
                element === 1 ? '/stories' : '/[currentSectionState]/[page]'
              }
              as={
                element === 1
                  ? '/stories'
                  : `/${currentSectionState}/${element}`
              }
            >
              <Box
                borderRadius={spacing.L.rem}
                px={spacing.M.rem}
                py={spacing.XS.rem}
                mr={spacing.XS.rem}
                _hover={{ cursor: 'pointer' }}
                backgroundColor={
                  element === pageNumber
                    ? color[colorMode].surface
                    : color[colorMode].top
                }
                _active={undefined}
                _focus={undefined}
              >
                <Link
                  _hover={undefined}
                  _focus={undefined}
                  href={
                    element === 1
                      ? '/stories'
                      : `/${currentSectionState}/${element}`
                  }
                  color={color[colorMode].text}
                  title={(element || '').toString()}
                >
                  {element}
                </Link>
              </Box>
            </NextLink>
          ),
        )}

        <NextLink
          href="/[currentSectionState]/[page]"
          visibility={pageNumber < data.numberOfPages ? 'visible' : 'hidden'}
          as={`/${currentSectionState}/${pageNumber + 1}`}
        >
          <Link
            _hover={undefined}
            _focus={undefined}
            href={`/${currentSectionState}/${pageNumber + 1}`}
            color={color[colorMode].text}
            title={(pageNumber + 1).toString()}
          >
            <Flex
              px={spacing.XS.rem}
              py={spacing.toRem(6)}
              mr={spacing.XS.rem}
              borderRadius={spacing.L.rem}
              align={'center'}
              justifyContent={'center'}
              color={color[colorMode].textSecondary}
            >
              <CaretRightSvg />
            </Flex>
          </Link>
        </NextLink>
      </Flex>
    );
  };

  const getPageTitle = (currentSection: KlList, pageNumber: number) => {
    let title = '';
    switch (currentSection) {
      case KlList.ARTICLES:
        title += 'Articles';
        break;
      case KlList.COLLECTIONS:
        title += 'Collections';
        break;
      case KlList.STORIES:
        title += 'Stories';
        break;
      default:
        break;
    }
    title += ' Library';

    if (pageNumber > 1) title += ' - Page ' + pageNumber;

    return title;
  };

  const getPageDescription = (currentSection: KlList) => {
    let description = '';
    switch (currentSection) {
      case KlList.ARTICLES:
        const { article1, domain1, domain2 } = getArticlesDescriptionItems();
        description += `Discover and read amazing articles like ${article1} from ${domain1} or others from ${domain2}.`;
        break;
      case KlList.COLLECTIONS:
        description +=
          'Read the collection of the week for free, or dive into the full list of collections with a Pro account.';
        break;
      case KlList.STORIES:
        const stories = (data as LatestList<StoryApiResponse>).list ?? [];
        if (stories.length > 0) {
          description += `Discover top stories from dozens of topics, including ${
            stories[0].title
          }, ${stories.length > 1 ? stories[1].title : ''} and more.`;
        }
        break;
      default:
        break;
    }
    return description;
  };

  const klPageMetadata: PageMetadata = {
    pageTitle: getPageTitle(currentSectionState, pageNumber),
    pageDescription: getPageDescription(currentSectionState),
    canonical:
      router.pathname === '/stories'
        ? 'https://deepstash.com/stories'
        : `https://deepstash.com/${currentSectionState}/${pageNumber}`,
    robotsTag: 'index',
  };

  return (
    <LayoutAdapter
      pageMetadata={klPageMetadata}
      withAdditionalInfoNsi
      disableProAd={
        router.pathname === '/stories' || router.pathname === '/stories/[page]'
      }
    >
      <Flex pt={isMobileView ? 0 : spacing.M.rem} flexDir={'column'}>
        {renderHeader()}
        {renderSection(currentSectionState)}
        {data.numberOfPages > 1 && renderPages()}
      </Flex>
    </LayoutAdapter>
  );
};

export default KnowledgeLibrary;
